import { useState, useMemo } from 'react'

/**
 * FilterPanel.jsx
 *
 * Stage 6 — Multi-select filter UI. Renders one collapsible group per
 * filter key (resource_type, indirect_category, cost_type, sub_code,
 * resource_code — whichever the current dataset supports, passed in as
 * `availableFilters`). Produces the Multi-select Filter Contract shape
 * (string | string[] | undefined) that every extended Adapter already
 * understands (Stage 1/2) — this component does not invent a different
 * filter representation, it only builds the object the Adapters expect.
 *
 * Options for each filter key come from `optionsByKey` — actual values
 * observed in a real query result, never a hardcoded/invented list.
 * Sub Code / Resource Code lists in particular can be large, so each
 * group includes an inline search box.
 */
export default function FilterPanel({ availableFilters, optionsByKey, filters, onChange }) {
  if (!availableFilters || availableFilters.length === 0) {
    return <div style={{ fontSize: 12, color: '#9CA3AF' }}>No filters available for this dataset.</div>
  }

  function toggleValue(key, value) {
    const current = filters[key]
    const currentArray = Array.isArray(current) ? current : (current && current !== 'all' ? [current] : [])
    const next = currentArray.includes(value)
      ? currentArray.filter(v => v !== value)
      : [...currentArray, value]
    onChange({ ...filters, [key]: next.length === 0 ? undefined : next })
  }

  function clearKey(key) {
    onChange({ ...filters, [key]: undefined })
  }

  function selectAllForKey(key, allOptions) {
    onChange({ ...filters, [key]: allOptions })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {availableFilters.map(key => (
        <FilterGroup
          key={key}
          filterKey={key}
          options={optionsByKey?.[key] || []}
          selected={filters[key]}
          onToggle={v => toggleValue(key, v)}
          onClear={() => clearKey(key)}
          onSelectAll={opts => selectAllForKey(key, opts)}
        />
      ))}
    </div>
  )
}

function FilterGroup({ filterKey, options, selected, onToggle, onClear, onSelectAll }) {
  const [search, setSearch] = useState('')
  const selectedArray = Array.isArray(selected) ? selected : (selected && selected !== 'all' ? [selected] : [])

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options
    const q = search.toLowerCase()
    return options.filter(o => o.toLowerCase().includes(q))
  }, [options, search])

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11.5, fontWeight: 700, color: '#374151' }}>{formatFilterLabel(filterKey)}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => onSelectAll(options)} style={miniButtonStyle}>All</button>
          <button onClick={onClear} style={miniButtonStyle}>Clear</button>
        </div>
      </div>

      {options.length > 8 && (
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={`Search ${formatFilterLabel(filterKey).toLowerCase()}…`}
          style={{ width: '100%', padding: '5px 8px', fontSize: 11.5, border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 6 }}
        />
      )}

      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {filteredOptions.length === 0 && (
          <div style={{ fontSize: 11, color: '#9CA3AF', padding: '4px 0' }}>No matching options.</div>
        )}
        {filteredOptions.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '3px 0', cursor: 'pointer' }}>
            <input type="checkbox" checked={selectedArray.includes(opt)} onChange={() => onToggle(opt)} />
            {opt}
          </label>
        ))}
      </div>

      {selectedArray.length > 0 && (
        <div style={{ fontSize: 10.5, color: '#1A7A4A', marginTop: 6 }}>{selectedArray.length} selected</div>
      )}
    </div>
  )
}

function formatFilterLabel(key) {
  const labels = {
    resource_type: 'Resource Type',
    indirect_category: 'Indirect Category',
    cost_type: 'Cost Type',
    sub_code: 'Sub Code',
    resource_code: 'Resource Code',
  }
  return labels[key] || key
}

const miniButtonStyle = {
  padding: '2px 8px', fontSize: 10, borderRadius: 4, border: '1px solid #E5E7EB',
  background: '#fff', cursor: 'pointer', color: '#6B7280',
}
